import React from "react";
import Control from "../../../assets/vfx/controls/control";
import { ControlDatagridActionButtonProps, ControlDataGridColumnProps, } from "../../../assets/vfx/controls/components/datagrid/controlDataGrid";
import { IControlStyles, useControlStyles, } from "../../../assets/vfx/controls/components";
import { LoadingIndicator, VfxLib } from "../../../assets/vfx";
import { ReferenceService } from "./referenceService";
import { createLayoutOptions } from "../../../assets/configuration/gridConfig";
import { ExpandButton } from "../../common/Buttons";
import { ReferenceGridDto, ReferenceViewDto, SearchReferenceDto, } from "../../../services/dto";
import { ServiceHelper } from "../../../assets/utils/helpers";
import { LINK_TYPE } from "./referenceContainer";
import { FontIcon } from "../../../assets/icons/FontIcon";
import type { IconTypeValue } from "../../../assets/icons/glyphMaps";
import { Constants } from "../../../assets/utils/lib/constants";
import { GRID_ACTION_TYPES } from "../../../assets/types";

interface ReferenceGridProps {
    controlStyles?: IControlStyles;
    searchQuery: SearchReferenceDto;
    setSearchQuery: React.Dispatch<React.SetStateAction<SearchReferenceDto>>;
    handleExpandRow: (rowData: ReferenceGridDto, rowId: string) => void;
    onCustomEvent?: (actionType: string, dataId: string) => void;
    linkType: string;
    isChildGrid?: boolean;
    filterContent?: React.ReactNode | undefined;
}

interface ReferenceGridColorFields extends ReferenceGridDto {
    iconBackgroundColor?: string | undefined;
    iconForeColor?: string | undefined;
}

const DATA_ID_FIELD = "referenceID";

const isLightHexColor = (color?: string): boolean => {
    const normalized = color?.trim().toLowerCase();
    if (!normalized || !/^#[0-9a-f]{6}$/.test(normalized)) return false;

    const red = parseInt(normalized.slice(1, 3), 16);
    const green = parseInt(normalized.slice(3, 5), 16);
    const blue = parseInt(normalized.slice(5, 7), 16);
    const luminance = (0.2126 * red + 0.7152 * green + 0.0722 * blue) / 255;

    return luminance > 0.88;
};

const getColorFields = (reference: ReferenceGridDto): ReferenceGridColorFields =>
    reference as ReferenceGridColorFields;

const getIconBackground = (reference: ReferenceGridDto): string | undefined => {
    const colorFields = getColorFields(reference);
    return colorFields.iconBackgroundColor;
};

const getIconForeground = (
    reference: ReferenceGridDto,
    backgroundColor?: string
): string => {
    const colorFields = getColorFields(reference);
    const foregroundColor = colorFields.iconForeColor ?? reference.iconColor ?? "var(--color-icon-muted)";

    if (isLightHexColor(backgroundColor) && isLightHexColor(foregroundColor)) {
        return "var(--color-accent-dark)";
    }

    return foregroundColor;
};

interface ResourceThumbProps {
    imagePath?: string;
    iconName?: string;
    iconType?: string;
    foregroundColor: string;
    backgroundColor?: string;
    isChildGrid?: boolean;
}

// These cells are rendered once via renderToString (see FormatCustomCell in
// controlDataGrid.tsx) and handed to DataTables/jQuery as static HTML,
// entirely outside React's render tree -- there is no live React runtime
// attached afterward. React state and event-handler *props* (useState,
// onError, onClick, etc.) either get silently dropped or, worse, can throw
// once DataTables invokes the renderer more than once internally, which can
// crash the whole app (an uncaught render error unmounts the entire React
// tree, not just this cell). This function must stay plain -- no hooks.
//
// To still show a fallback icon when an image 404s, we render both the
// <img> and the fallback icon into the static HTML up front (fallback
// hidden via inline style) and use a raw, lowercase `onerror` HTML
// attribute -- not React's `onError` prop -- so it survives renderToString
// as literal markup and runs as plain browser JS on failure, the same way
// handleExpandClick in controlDataGrid.tsx already manipulates classList
// directly with no React involved.
const escapeHtmlAttribute = (value: string): string =>
    value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");

const renderResourceThumb = (props: ResourceThumbProps): React.JSX.Element => {
    const hasImage = !!props.imagePath;

    if (hasImage) {
        const src = escapeHtmlAttribute(ServiceHelper.GetImageUrl(props.imagePath as string));
        // On failure: hide the <img>, reveal the sibling fallback icon. Both
        // elements already exist in the markup; nothing here needs React.
        const html =
            `<img src="${src}" alt="" ` +
            `onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';" />` +
            `<i class="bi bi-image" style="display:none;color:var(--color-icon-muted);font-size:22px;"></i>`;

        return (
            <span
                className="vfx-datagrid-resource-thumb"
                dangerouslySetInnerHTML={{ __html: html }}
            />
        );
    }

    return (
        <span
            className="vfx-datagrid-resource-thumb"
            style={{
                background: props.backgroundColor,
                border:
                    props.backgroundColor && isLightHexColor(props.backgroundColor)
                        ? "1px solid var(--color-divider-subtle)"
                        : undefined,
            }}
        >
            {props.iconName && props.iconType ? (
                <FontIcon
                    type={props.iconType as IconTypeValue}
                    name={props.iconName}
                    size={props.isChildGrid ? 20 : 26}
                    style={{ color: props.foregroundColor }}
                />
            ) : (
                <i
                    className="bi bi-image"
                    style={{ color: "var(--color-icon-muted)", fontSize: 22 }}
                />
            )}
        </span>
    );
};

const ReferenceGrid = (props: ReferenceGridProps) => {
    const styles: IControlStyles = props.controlStyles ?? useControlStyles();
    const referenceGridQuery = ReferenceService.gridQuery(props.searchQuery);
    const references: ReferenceGridDto[] = referenceGridQuery.data?.result ?? [];
    const hasData: boolean = references.length > 0;
    const showImage = props.linkType !== LINK_TYPE.NAV_MENU_ITEM;

    const renderResourceCell = (
        id: string,
        rowData: ReferenceGridDto
    ): React.JSX.Element => {
        const backgroundColor = getIconBackground(rowData);
        const foregroundColor = getIconForeground(rowData, backgroundColor);

        return (
            <div
                className="vfx-datagrid-resource-cell"
                style={{ gridTemplateColumns: showImage ? undefined : "minmax(0, 1fr)" }}
            >
                {showImage &&
                    renderResourceThumb({
                        imagePath: rowData.imagePath,
                        iconName: rowData.iconName,
                        iconType: rowData.iconType,
                        foregroundColor,
                        backgroundColor,
                        isChildGrid: props.isChildGrid,
                    })}
                <div>
                    <p className="vfx-datagrid-resource-name">
                        {rowData.display ?? "Untitled"}
                    </p>
                    {rowData.description && (
                        <p className="vfx-datagrid-resource-description">
                            {rowData.description}
                        </p>
                    )}
                </div>
            </div>
        );
    };

    const createGridColumns = (): ControlDataGridColumnProps[] => [
        {
            data: DATA_ID_FIELD,
            title: "ID",
            visible: false,
            sortable: false,
            columnType: "number",
            alignment: "right",
        },
        {
            alignment: "center",
            data: DATA_ID_FIELD,
            title: "",
            columnType: "custom",
            sortable: false,
            visible: props.isChildGrid !== true,
            customCell: (id: string, rowData: ReferenceGridDto) =>
                rowData?.hasChildren ? <ExpandButton /> : <></>,
            width: "5%",
        },
        {
            data: DATA_ID_FIELD,
            title: "",
            columnType: "custom",
            sortable: false,
            visible: props.isChildGrid === true,
            customCell: (_id: string, rowData: ReferenceGridDto) =>
                rowData?.hasChildren ? (
                    <ExpandButton />
                ) : (
                    <i className="bi bi-grip-vertical" style={{ color: "var(--color-icon-muted)" }} />
                ),
            width: "5%",
        },
        {
            data: "display",
            title: "Resource",
            visible: true,
            columnType: "custom",
            alignment: "left",
            sortable: true,
            customCell: renderResourceCell,
            width: "62%",
        },
        {
            data: "sortValue",
            title: "Display Order",
            visible: true,
            sortable: true,
            columnType: "text",
            alignment: "left",
            width: "8%",
        },
    ];

    if (referenceGridQuery?.isLoading) {
        return <LoadingIndicator isFullPage={!props.isChildGrid} />;
    }

    const gridColumns: ControlDataGridColumnProps[] = createGridColumns();
    const layoutOptions = props.isChildGrid
        ? {
            displayPageInfo: false,
            displayPageLength: false,
            displayPaging: false,
        }
        : createLayoutOptions(hasData);

    function actionButtons(row: ReferenceViewDto) {
        const actionButton: ControlDatagridActionButtonProps[] = [
            VfxLib.datagridActionButtons({ datagridStyles: styles }).edit,
            VfxLib.datagridActionButtons({ datagridStyles: styles }).view_delete,
            VfxLib.datagridActionButtons({ datagridStyles: styles }).new_sibling,
        ];
        if (row.url) {
            actionButton.splice(2, 1);
        }
        return actionButton;
    }

    return (
        <Control.DataGrid
            key={JSON.stringify(layoutOptions)}
            dataId={DATA_ID_FIELD}
            cols={gridColumns}
            data={references}
            isActionButtonClick={true}
            actionCol={{
                data: DATA_ID_FIELD,
                title: "Actions",
                visible: true,
                columnType: "action",
                alignment: "left",
                width: "20%",
                controlStyles: styles,
                actionButtons: (row) => actionButtons(row),
            }}
            layoutOptions={layoutOptions}
            totalRecords={referenceGridQuery.data?.totalSize ?? references?.length ?? 0}
            query={props.searchQuery}
            onCustomEvent={props.onCustomEvent}
            onParentRowClick={props.handleExpandRow}
            onQueryChange={(patch) =>
                props.setSearchQuery((query) => ({ ...query, ...patch }))
            }
            behaviorOptions={{ responsive: false, scrollX: false }}
            uiOptions={{
                title: props.isChildGrid ? undefined : "All resources",
                subtitle: props.isChildGrid
                    ? undefined
                    : "View and manage all resources in your organization.",
                filterContent: props.isChildGrid ? undefined : props.filterContent,
                searchEnabled: !props.isChildGrid,
                searchPlaceholder: "Search resources...",
                primaryAction: props.isChildGrid
                    ? undefined
                    : {
                        text: "Add resource",
                        icon: "bi bi-plus-lg",
                        onClick: () =>
                            props.onCustomEvent?.(GRID_ACTION_TYPES.NEW, Constants.emptyGuid),
                    },
                isChildGrid: props.isChildGrid,
                childTitle: "Child resources",
                childCount: referenceGridQuery.data?.totalSize ?? references.length,
                footerAction:
                    props.isChildGrid && props.searchQuery.parentId
                        ? {
                            text: "Add child resource",
                            icon: "bi bi-plus-lg",
                            onClick: () =>
                                props.onCustomEvent?.(
                                    GRID_ACTION_TYPES.NEW_SIBLING,
                                    props.searchQuery.parentId ?? Constants.emptyGuid
                                ),
                        }
                        : undefined,
            }}
        />
    );
};

export default ReferenceGrid;